import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { adminApi } from '../../../api';
import { ROLE_KEYS } from '../../../utils/roleUtils';

const ClientsPage = ({ sessionName = '' }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const normalizeRole = (value) => {
    if (!value) return '';
    const raw = String(value).trim().toLowerCase();
    if (!raw) return '';
    const condensed = raw.replace(/[^a-z0-9]/g, '');
    if (condensed === 'superadmin') return ROLE_KEYS.SUPER_ADMIN;
    if (condensed === 'admin') return ROLE_KEYS.ADMIN;
    if (condensed === 'agentadmin') return ROLE_KEYS.AGENT_ADMIN;
    if (condensed === 'agent') return ROLE_KEYS.AGENT;
    if (condensed === 'client') return ROLE_KEYS.CLIENT;
    return raw;
  };

  useEffect(() => {
    const loadClients = async () => {
      setLoading(true);
      setErrorMsg('');
      try {
        const response = await adminApi.getClients();
        const result = response?.data?.data || response?.data || [];
        setClients(Array.isArray(result) ? result : []);
      } catch (error) {
        setErrorMsg(error?.response?.data?.error || 'Failed to load clients');
        setClients([]);
      } finally {
        setLoading(false);
      }
    };

    loadClients();
  }, []);

  const filteredData = useMemo(() => {
    const search = searchTerm.toLowerCase();
    return clients
      .filter((item) => normalizeRole(item.u_role) === ROLE_KEYS.CLIENT)
      .filter((item) =>
        [
          item.u_name,
          item.u_email,
          item.u_mobile,
          item.u_role,
        ]
          .filter(Boolean)
          .some((val) => String(val).toLowerCase().includes(search))
      );
  }, [clients, searchTerm]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  };

  const renderStatus = (client) => {
    const statusValue = client.u_status ?? client.u_active;
    return statusValue === 1 ? 'Active' : 'Inactive';
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" mb={2}>
        <Typography variant="h5">Clients</Typography>
        <Typography variant="body2" color="text.secondary">
          {sessionName}
        </Typography>
      </Stack>

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMsg}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <TextField
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              size="small"
              sx={{ maxWidth: 320 }}
            />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Stack alignItems="center" spacing={1} py={4}>
                          <CircularProgress size={28} />
                          <Typography variant="body2" color="text.secondary">
                            Loading clients...
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : currentData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography align="center" color="text.secondary">
                          No clients found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    currentData.map((item, index) => (
                      <TableRow key={item.uid || index}>
                        <TableCell>{startIndex + index + 1}</TableCell>
                        <TableCell>{item.u_name || '-'}</TableCell>
                        <TableCell>{item.u_email || '-'}</TableCell>
                        <TableCell>{item.u_mobile || '-'}</TableCell>
                        <TableCell>{item.u_role || '-'}</TableCell>
                        <TableCell>{renderStatus(item)}</TableCell>
                        <TableCell>{formatDate(item.u_created)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPages > 1 && (
              <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCurrentPage((prev) => prev - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Typography variant="body2">
                  Page {currentPage} of {totalPages}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClientsPage;
